/**
 * Team Bio Modal
 * Handles opening, closing, and navigating through team member bios
 */

class TeamModal {
  constructor() {
    this.modal = document.getElementById('teamBioModal');
    if (!this.modal) return;

    this.memberCards = Array.from(document.querySelectorAll('.team-section__card[data-member-bio]'));
    this.currentIndex = 0;
    this.totalMembers = this.memberCards.length;

    // Modal elements
    this.overlay = this.modal.querySelector('.team-modal__overlay');
    this.closeBtn = this.modal.querySelector('.js-close-modal');
    this.prevBtn = this.modal.querySelector('.js-prev-member');
    this.nextBtn = this.modal.querySelector('.js-next-member');
    
    // Content elements
    this.modalPhoto = document.getElementById('modalMemberPhoto');
    this.modalRole = document.getElementById('modalMemberRole');
    this.modalName = document.getElementById('modalMemberName');
    this.modalBio = document.getElementById('modalMemberBio');
    this.modalEmail = document.getElementById('modalMemberEmail');
    this.modalPhone = document.getElementById('modalMemberPhone');
    this.modalEmailWrapper = document.getElementById('modalEmailWrapper');
    this.modalPhoneWrapper = document.getElementById('modalPhoneWrapper');
    this.modalCurrentIndex = document.getElementById('modalCurrentIndex');
    this.modalTotalMembers = document.getElementById('modalTotalMembers');

    this.init();
  }

  init() {
    // Add click handlers to "Read Full Bio" buttons and photos
    this.memberCards.forEach((card, index) => {
      const bioBtn = card.querySelector('.js-open-bio');
      if (bioBtn) {
        bioBtn.addEventListener('click', () => this.openModal(index));
      }
      
      // Make photo clickable
      const photo = card.querySelector('.team-section__photo');
      if (photo) {
        photo.addEventListener('click', () => this.openModal(index));
      }
    });

    // Close modal handlers
    this.closeBtn?.addEventListener('click', () => this.closeModal());
    this.overlay?.addEventListener('click', () => this.closeModal());

    // Navigation handlers
    this.prevBtn?.addEventListener('click', () => this.showPrevMember());
    this.nextBtn?.addEventListener('click', () => this.showNextMember());

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (this.modal.style.display === 'none') return;

      if (e.key === 'Escape') {
        this.closeModal();
      } else if (e.key === 'ArrowLeft') {
        this.showPrevMember();
      } else if (e.key === 'ArrowRight') {
        this.showNextMember();
      }
    });

    // Update total members counter
    if (this.modalTotalMembers) {
      this.modalTotalMembers.textContent = this.totalMembers;
    }
  }

  openModal(index) {
    this.currentIndex = index;
    this.updateModalContent();
    this.modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';
    document.getElementById('masthead').classList.add('header-hidden');
    
    // Trap focus in modal
    this.trapFocus();
  }

  closeModal() {
    this.modal.style.display = 'none';
    document.body.style.overflow = '';
  }

  showPrevMember() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updateModalContent();
    }
  }

  showNextMember() {
    if (this.currentIndex < this.totalMembers - 1) {
      this.currentIndex++;
      this.updateModalContent();
    }
  }

  updateModalContent() {
    const card = this.memberCards[this.currentIndex];
    if (!card) return;

    // Get member data from card attributes
    const name = card.dataset.memberName;
    const role = card.dataset.memberRole;
    const photo = card.dataset.memberPhoto;
    const email = card.dataset.memberEmail;
    const phone = card.dataset.memberPhone;
    const bio = card.dataset.memberBio;

    // Update content
    if (this.modalPhoto) {
      this.modalPhoto.src = photo;
      this.modalPhoto.alt = name;
    }

    if (this.modalRole) {
      this.modalRole.textContent = role.toUpperCase();
    }

    if (this.modalName) {
      this.modalName.textContent = name;
    }

    if (this.modalBio) {
      // Convert line breaks and format bio text
      const formattedBio = bio.split('\n\n').map(para => `<p>${para}</p>`).join('');
      this.modalBio.innerHTML = formattedBio;
    }

    // Update contact info
    if (email && this.modalEmail && this.modalEmailWrapper) {
      this.modalEmail.textContent = 'Get in Touch';
      this.modalEmail.href = `mailto:hello@stableandvine.com?cc=${encodeURIComponent(email)}`;
      this.modalEmailWrapper.style.display = 'flex';
    } else if (this.modalEmailWrapper) {
      this.modalEmailWrapper.style.display = 'none';
    }

    if (phone && this.modalPhone && this.modalPhoneWrapper) {
      this.modalPhone.textContent = phone;
      this.modalPhone.href = `tel:${phone.replace(/[^0-9+]/g, '')}`;
      this.modalPhoneWrapper.style.display = 'flex';
    } else if (this.modalPhoneWrapper) {
      this.modalPhoneWrapper.style.display = 'none';
    }

    // Update counter
    if (this.modalCurrentIndex) {
      this.modalCurrentIndex.textContent = this.currentIndex + 1;
    }

    // Update navigation button states
    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentIndex === 0;
    }

    if (this.nextBtn) {
      this.nextBtn.disabled = this.currentIndex === this.totalMembers - 1;
    }
  }

  trapFocus() {
    // Get all focusable elements in the modal
    const focusableElements = this.modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Focus the close button
    if (this.closeBtn) {
      this.closeBtn.focus();
    }

    // Trap focus within modal
    this.modal.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          lastElement.focus();
          e.preventDefault();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          firstElement.focus();
          e.preventDefault();
        }
      }
    });
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new TeamModal();
});
